import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutData } from '@/mocks/videos';

export default function AboutPage() {
  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '棒棒TV',
    url: 'https://www.bangbangtv.com/',
    description: aboutData.brandIntro,
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: aboutData.contact.phone,
      email: aboutData.contact.email,
      contactType: 'customer service',
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: aboutData.contact.address,
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: aboutData.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="关于我们"
        description={`棒棒TV - ${aboutData.brandIntro.substring(0, 120)}`}
        keywords="棒棒TV,关于我们,免费高清视频,在线不缓冲秒播,品牌介绍,联系方式"
        canonicalUrl="https://www.bangbangtv.com/about"
      />
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="w-full px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />

            <section className="pb-10 md:pb-14">
              <div className="text-center mb-8 md:mb-12">
                <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 font-heading mb-3">
                  关于棒棒TV
                </h1>
                <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto">
                  中国领先的免费高清视频在线播放平台
                </p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6 mb-10 md:mb-14">
                {aboutData.stats.map((stat) => (
                  <div key={stat.label} className="text-center p-5 md:p-6 bg-background-100 rounded-lg">
                    <div className="text-2xl md:text-3xl font-bold text-primary-500 font-heading">
                      {stat.value}
                    </div>
                    <div className="text-sm text-foreground-500 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>

              <div className="max-w-3xl mx-auto">
                <h2 className="text-lg md:text-xl font-semibold text-foreground-900 font-heading mb-4">
                  品牌故事
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6">
                  {aboutData.brandIntro}
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  {aboutData.mission}
                </p>
              </div>
            </section>

            <section className="py-10 md:py-14 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6">
              <div className="text-center mb-6 md:mb-10">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
                  核心优势
                </h2>
                <p className="text-sm text-foreground-500 mt-1">四大核心能力，打造极致观影体验</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {aboutData.features.map((feature) => (
                  <div key={feature.title} className="flex flex-col items-center text-center p-5 md:p-6 bg-background-50 rounded-lg">
                    <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center bg-accent-100 rounded-2xl mb-4">
                      <i className={`${feature.icon} text-2xl text-accent-500`}></i>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-900 mb-2">{feature.title}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{feature.description}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="py-10 md:py-14" id="testimonials">
              <div className="text-center mb-6 md:mb-10">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
                  用户口碑
                </h2>
                <p className="text-sm text-foreground-500 mt-1">听听用户怎么说</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {aboutData.testimonials.map((t, i) => (
                  <div key={i} className="p-5 md:p-6 bg-background-100 rounded-lg">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }).map((_, idx) => (
                        <i
                          key={idx}
                          className={`ri-star-fill text-sm ${idx < Math.floor(t.rating) ? 'text-accent-500' : 'text-foreground-300'}`}
                        ></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                      "{t.text}"
                    </p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center bg-accent-100 rounded-full">
                        <i className={`${t.avatar} text-accent-600 text-sm`}></i>
                      </div>
                      <span className="text-sm font-medium text-foreground-900">{t.name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="py-10 md:py-14 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6" id="faq">
              <div className="text-center mb-6 md:mb-10">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
                  常见问题
                </h2>
                <p className="text-sm text-foreground-500 mt-1">关于棒棒TV，你可能想了解</p>
              </div>
              <div className="max-w-2xl mx-auto flex flex-col gap-3">
                {aboutData.faqs.map((faq, i) => (
                  <details key={i} className="group bg-background-50 rounded-lg p-4 md:p-5 cursor-pointer">
                    <summary className="flex items-center justify-between text-sm md:text-base font-medium text-foreground-900 list-none">
                      {faq.question}
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform text-lg"></i>
                    </summary>
                    <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </details>
                ))}
              </div>
            </section>

            <section className="py-10 md:py-14" id="contact">
              <div className="text-center mb-6 md:mb-10">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-500 mt-1">我们随时为您服务</p>
              </div>
              <div className="max-w-2xl mx-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
                  <div className="flex items-start gap-3 p-4 md:p-5 bg-background-100 rounded-lg">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg shrink-0">
                      <i className="ri-mail-line text-primary-500"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">电子邮件</h4>
                      <p className="text-sm text-foreground-600">{aboutData.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 md:p-5 bg-background-100 rounded-lg">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg shrink-0">
                      <i className="ri-phone-line text-primary-500"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">客服电话</h4>
                      <p className="text-sm text-foreground-600">{aboutData.contact.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 md:p-5 bg-background-100 rounded-lg">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg shrink-0">
                      <i className="ri-wechat-line text-primary-500"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">微信公众号</h4>
                      <p className="text-sm text-foreground-600">{aboutData.contact.wechat}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 md:p-5 bg-background-100 rounded-lg">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg shrink-0">
                      <i className="ri-weibo-line text-primary-500"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">微博</h4>
                      <p className="text-sm text-foreground-600">{aboutData.contact.weibo}</p>
                    </div>
                  </div>
                </div>
                <div className="mt-6 p-4 md:p-5 bg-background-100 rounded-lg flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg shrink-0">
                    <i className="ri-map-pin-line text-primary-500"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">公司地址</h4>
                    <p className="text-sm text-foreground-600">{aboutData.contact.address}</p>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}